'use strict';

import axios from 'axios';

class LimaWebApi {
  constructor({axiosOpts} = {}) {
    this.axios = axios.create(_.merge({
      timeout: 30000,
      maxRedirects: 10,
      maxContentLength: 50 * 1000 * 1000,
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'Accept': 'application/json; charset=UTF-8'
      },
      // baseURL: '/api/v1/'
    }, axiosOpts));
    // provide aliases
    _.forEach(['delete', 'get', 'head', 'options'], (method) => {
      this[method] = (url, opts = {}) => this.request(_.merge(opts, {method, url}));
    });
    _.forEach(['post', 'put', 'patch'], (method) => {
      this[method] = (url, data = {}, opts = {}) => this.request(_.merge(opts, {method, data, url}));
    });
  }
  request(opts = {}) {
    // console.log('LimaApi', this, opts);
    const token = this.token;
    if (token) {
      opts.headers = opts.headers ?? {};
      opts.headers.Authorization = `Bearer ${token}`;
    }
    return this.axios.request(opts).catch((err) => {
      this.catchWebApiError(err);
      throw err;
    });
  }
  catchWebApiError(err) {
    // ... to be overridden
  }
  get token() {
    try {
      const mr = document.cookie.match(/^.*token=([^;]+).*$/);
      const token = (mr && mr.length === 2) ? mr[1] : '';
      return typeof token === 'string' ? (token.startsWith('%22') ? JSON.parse(decodeURIComponent(token)) : token) : '';
    } catch (err) {
      console.warn(err);
      return '';
    }
  }
  set token(value) {
    try {
      value = (value && typeof value === 'string') ? encodeURIComponent(value) : '';
      document.cookie = [`token=${value}`, 'path=/', ...(value ? [] : ['expires=Thu, 01 Jan 1970 00:00:00 GMT'])].join('; ') + ';';
    } catch (err) {
      console.warn(err);
    }
  }
  encodeQuery(val) {
    return encodeURIComponent(val).replace(/%40/gi, '@').replace(/%3A/gi, ':')
      .replace(/%24/g, '$').replace(/%2C/gi, ',').replace(/%3B/gi, ';');
  }
  makeQueryArray(obj, ...arr) {
    let queries = [];
    _.forEach(obj, (val, key) => {
      switch (typeof val) {
        case 'boolean': {
          if (val) queries.push(`${this.encodeQuery(key)}`); // write only key itself if val is true
          return;
        }
        case 'string': case 'number': {
          return queries.push(`${this.encodeQuery(key)}=${this.encodeQuery('' + val)}`);
        }
        case 'object': {
          if (val && val instanceof Date) {
            return queries.push(`${this.encodeQuery(key)}=${this.encodeQuery(val.toISOString())}`);
          }
          try {
            return queries.push(`${this.encodeQuery(key)}=${this.encodeQuery(JSON.stringify(val))}`);
          } catch (ex) {
          }
        }
      }
    });
    _.forEach(arr, val => {
      if (!val) return;
      if (typeof val === 'string') queries.push(val);
      else if (Array.isArray(val)) queries.push(...this.makeQueryArray({}, ...val));
      else if (typeof val === 'object') queries.push(...this.makeQueryArray(val));
    });
    return queries;
  }
  query(options, ...others) {
    const queries = this.makeQueryArray(options, ...others) || [];
    return queries.length ? `?${queries.join('&')}` : '';
  }
  postAuth({model, strategy} = {}) {
    return this.post(`auth/${strategy}`, model).then(res => res.data);
  }
  getCurrentUser() {
    return this.get('/api/v1/users/me').then(res => res.data);
  }
  deleteCurrentUser() {
    return this.delete('/api/v1/users/me').then(res => res.data);
  }
  putUserPassword({id, model} = {}) {
    return this.put(`/api/v1/users/${encodeURIComponent(id)}/reset_password`, model).then(res => res.data);
  }
  putPassword({id, model} = {}) {
    return this.put(`/api/v1/users/${encodeURIComponent(id)}/password`, model).then(res => res.data);
  }
  getUserProperty({id, name} = {}) {
    return this.get(`/api/v1/users/${encodeURIComponent(id)}/properties/${encodeURIComponent(name)}`).then(res => res.data);
  }
  putUserProperty({id, name, model} = {}) {
    return this.put(`/api/v1/users/${encodeURIComponent(id)}/properties/${encodeURIComponent(name)}`, model).then(res => res.data);
  }
  getSystemProperties({parseValue = false} = {}) {
    return this.get(`/api/v1/system/${this.query({parseValue})}`).then(res => res.data);
  }
  getSystemProperty({name = '', parseValue = false} = {}) {
    return this.get(`/api/v1/system/${encodeURIComponent(name)}${this.query({parseValue})}`).then(res => res.data);
  }
  deleteSystemProperty({name = ''} = {}) {
    return this.delete(`/api/v1/system/${encodeURIComponent(name)}`).then(res => res.data);
  }
  putSystemProperty({model} = {}) {
    return this.put(`/api/v1/system`, model).then(res => res.data);
  }
  getSystemInfo() {
    return this.get(`/api/v1/system-info`).then(res => res.data);
  }
  getEventLogs({count, offset, limit, sort = {by:'', order:'asc'}, startDate, endDate} = {}) {
    const model = {count, offset, limit, startDate, endDate};
    if (sort.by && sort.order) model[`${sort.order}${_.capitalize(sort.by)}`] = true;
    return this.get(`/api/v1/event-logs${this.query(model)}`).then(res => res.data);
  }
  getUsers({functions = false} = {}) {
    return this.get(`/api/v1/user/accounts${this.query({functions})}`).then(res => res.data);
  }
  postUser({model} = {}) {
    return this.post('/api/v1/user/accounts', model).then(res => res.data);
  }
  putUser({id, model, force = false} = {}) {
    return this.put(`/api/v1/user/accounts/${encodeURIComponent(id)}${this.query({force})}`, model).then(res => res.data);
  }
  deleteUser({id, force = false} = {}) {
    return this.delete(`/api/v1/user/accounts/${encodeURIComponent(id)}${this.query({force})}`).then(res => res.data);
  }
  getUserGroups({functions = false} = {}) {
    return this.get(`/api/v1/user/groups${this.query({functions})}`).then(res => res.data);
  }
  postUserGroup({model} = {}) {
    return this.post('/api/v1/user/groups', model).then(res => res.data);
  }
  putUserGroup({id, model, force = false} = {}) {
    return this.put(`/api/v1/user/groups/${encodeURIComponent(id)}${this.query({force})}`, model).then(res => res.data);
  }
  deleteUserGroup({id, force = false} = {}) {
    return this.delete(`/api/v1/user/groups/${encodeURIComponent(id)}${this.query({force})}`).then(res => res.data);
  }
  getUserRoles({} = {}) {
    return this.get('/api/v1/user/roles').then(res => res.data);
  }
  tsunagaruPost({} = {}) {
    return this.post('https://mt1.tsunagaru.dev1.mediaedge.jp/auth/local').then(res => res.data);
  }
}

class State {
  _user = {};
  constructor() {
    this.returnPath = '';
    this.loginMessage = '';
  }
  set user(value) {
    // console.log('set user', JSON.stringify(value, null, 4));
    this._user = value ?? {};
  }
  get currentUser() {
    return this._user ?? {};
  }
  get isLoggedIn() {
    return this.currentUser.hasOwnProperty('roles');
  }
  hasFunction(required) {
    if (!required) return true;
    const requested = Array.isArray(required) ? required : [required];
    const funcs = _.get(this, 'currentUser.functions', []);
    // console.log('hasFunction', requested, funcs, _.every(requested, requested => _.includes(funcs, requested)));
    return _.every(requested, requested => _.includes(funcs, requested));
  }
}

class Socket {
  static _callId = 0;
  _calls = [];
  _subscriptions = [];
  _emits = [];
  _token = '';
  constructor({sessionExpired = () => {}} = {}) {
    this.socketIo = null;
    this.sessionExpired = sessionExpired;
  }
  get ready() {
    return !!this.socketIo;
  }
  async connect({token} = {}) {
    return new Promise((resolve, reject) => {
      try {
        this._token = token = token ?? this._token;
        if (this.socketIo) return this.socketIo;
        const opts = {path:'/socket.io-client'};
        if (token) opts.query = `token=${token}`;
        const socketIo = io('', opts);
        socketIo.once('connect', (...args) => { this.socketIo = socketIo; resolve(...args); });
        socketIo.once('connect_error', (...args) => { console.warn('socket.connect error: ', ...args); reject(...args); });
        socketIo.on('lima:call-response', (...args) => this._callResponse(...args));
        _.forEach(this._subscriptions, subscription => { socketIo.on.call(socketIo, subscription.name, subscription.handler); });
        _.forEach(this._emits, emit => { if (emit.timeout > Date.now()) socketIo.emit.call(socketIo, emit.name, emit.data); });
        this._emits = [];
      } catch (err) {
        console.warn('socket.connect error: ', err);
        reject(err);
      }
    });
  }
  async disconnect() {
    return new Promise((resolve, reject) => {
      try {
        this._token = '';
        const socketIo = this.socketIo;
        this.socketIo = null;
        const calls = this._calls.splice(0, this._calls.length);
        _.forEach(calls, call => {
          if (call && typeof call === 'object') {
            clearTimeout(call.timeout);
            call.reject('disconnected');
          }
        });
        if (socketIo) {
          socketIo.once('disconnect', (...args) => resolve(...args));
          socketIo.disconnect();
        } else {
          resolve();
        }
      } catch (err) {
        console.warn('socket.disconnect error: ', err);
        reject(err);
      }
    });
  }
  on(name, handler) {
    if (!name || typeof name !== 'string' || typeof handler !== 'function') return;
    this._subscriptions.push({name, handler});
    if (!this.socketIo) return;
    this.socketIo.on.call(this.socketIo, name, handler);
  }
  once(name, handler) {
    if (!name || typeof name !== 'string' || typeof handler !== 'function') return;
    if (this.socketIo) this.socketIo.once.call(this.socketIo, name, handler);
  }
  off(name, handler) {
    _.remove(this._subscriptions, {name, handler});
    if (this.socketIo) this.socketIo.removeListener.call(this.socketIo, name, handler);
  }
  emit(name, data, timeout = 30000) {
    if (!name || typeof name !== 'string') return;
    if (this.socketIo) { this.socketIo.emit.call(this.socketIo, name, data); return; }
    if (typeof timeout !== 'number') return;
    this._emits = _.reduce(this._emits, (emits, emit) => { if (emit.timeout > Date.now()) emits.push(emit); return emits; }, []);
    this._emits.push({name, data, timeout:Date.now() + timeout});
  }
  call(name, data, timeout = 30000, noExpiration = false) {
    return this._call(noExpiration ? 'call-request-no-expiration' : 'call-request', name, data, timeout);
  }
  callNoExpiration(name, data, timeout) {
    return this._call('call-request-no-expiration', name, data, timeout);
  }
  startTransaction(timeout = 30000) {
    return this._call('call-transaction', 'start', {}, timeout);
  }
  commitTransaction(timeout = 30000) {
    return this._call('call-transaction', 'commit', {}, timeout);
  }
  rollbackTransaction(timeout = 30000) {
    return this._call('call-transaction', 'rollback', {}, timeout);
  }
  _call(prefix, name, data, timeout) {
    return new Promise((resolve, reject) => {
      const call = {id:++Socket._callId, resolve, reject};
      this._calls.push(call);
      call.timeout = setTimeout(() => (_.remove(this._calls, {id:call.id})[0] ?? Promise).reject('timeout'), timeout);
      this.emit(`lima:${prefix}:${name}`, {id:call.id, data}, timeout);
    });
  }
  _callResponse(res) {
    if (!res || typeof res !== 'object') return;
    const call = _.remove(this._calls, {id:res.id})[0];
    if (!call || typeof call !== 'object') return;
    clearTimeout(call.timeout);
    if (res.err) {
      if (typeof res.err === 'object' && res.err.code === 401) {
        this.sessionExpired({msg:res.err.message});
      }
      call.reject(res.err);
    } else {
      call.resolve(res.data);
    }
  }
}

class LimaApi extends LimaWebApi {
  _systemProperties = null;
  _ping = 0;
  constructor() {
    super();
    this.state = new State();
    this.socket = new Socket({sessionExpired:(...args) => this.sessionExpired(...args)});
    this.router = null;
    this.socket.on('system-properties:changed', (...args) => this.systemPropertiesChanged());
    this.socket.on('user:last-login-time-changed', (...args) => this.socket.call('ping', {}, 1000).catch(err => {}));
  }
  systemPropertiesChanged() {
    this._systemProperties = null;
    this.updateStyle();
  }
  async ping() {
    const ping = ++this._ping;
    if (typeof this.state.currentUser.expiresInMs !== 'number') {
      // 'currentUser.expiresInMs' would be set if the user has login-hours limitation
      return;
    }
    // call 'ping' regularly to go to the login page when the session expired
    await new Promise(resolve => setTimeout(resolve, 60000));
    while (ping === this._ping) {
      try {
        await this.socket.call('ping', {}, 1000);
      } catch (err) {
        if (_.get(err, 'code') === 401) break; // session expired
      }
      await new Promise(resolve => setTimeout(resolve, 10000));
    }
  }
  async updateStyle() {
    const data = await this.systemProperties();
    if (!data['setting.style.id']) return;
    const styleId = `#${data['setting.style.id']}`;
    const $styles = $('.lima-bootstrap-style');
    if (!$styles.length) return;
    $styles.filter(styleId).prop('disabled', false);
    $styles.not(styleId).prop('disabled', true);
  }
  async updateCurrentUser() {
    await this.socket.disconnect();
    this.state.user = await super.getCurrentUser();
    if (this.state.isLoggedIn) await this.socket.connect({token:this.token}).then(() => this.ping(), err => {});
    this.systemProperties({refresh:true});
  }
  async tokenLogin({token} = {}) {
    await this.logout();
    this.token = token;
    await this.updateCurrentUser();
  }
  async logout() {
    await this.socket.disconnect();
    await super.deleteCurrentUser().catch(err => console.warn(err));
    this.token = null;
    this.state.user = {};
    this.state.returnPath = '';
    this.state.loginMessage = '';
    this._systemProperties = null;
  }
  systemProperties({refresh = false} = {}) {
    const getSystemProperties = async () => {
      const getData = () => this.state.isLoggedIn ? this.getSystemProperties({parseValue:true}) : super.getSystemProperties({parseValue:true});
      return getData().then(data => {
        const systemProperties = _.reduce(data, (data, {name, value}) => {
          data[name] = value;
          return data;
        }, {});
        // console.log('systemProperties', systemProperties);
        return systemProperties;
      });
    };
    if (refresh) this._systemProperties = null;
    return this._systemProperties = this._systemProperties ?? getSystemProperties();
  }
  async sessionExpired({msg} = {}) {
    if (_.get(this, 'router.currentRoute.path', '/') === '/login') return;
    await this.logout();
    this.state.returnPath = _.get(this, 'router.currentRoute.fullPath', '/');
    if (msg === 'The same user has logged in from a different terminal.') {
      this.state.loginMessage = msg;
    } else {
      this.state.loginMessage = 'Login session has expired';
    }
    if (!this.router) return;
    await this.router.push('/login').catch(err => {});
  }
  async passwordExpired() {
    if (_.get(this, 'router.currentRoute.path', '/') === '/settings') return;
    this.state.returnPath = _.get(this, 'router.currentRoute.fullPath', '/');
    this.state.loginMessage = 'Password has expired';
    if (!this.router) return;
    await this.router.push('/settings').catch(err => {});
  }
  /** method override
  */
  catchWebApiError(err) {
    // console.log('catchWebApiError', _.get(err, 'response.status'), _.get(err, 'response.data.message'));
    if (_.get(err, 'response.status') === 401) {
      this.sessionExpired();
    }
    if (_.get(err, 'response.status') === 403 && _.get(err, 'response.data.message') === 'Password has expired') {
      this.passwordExpired();
    }
  }
  getSystemProperties({parseValue = false} = {}) {
    return this.socket.ready ? this.socket.call('v1:system:system-property:findAll', {parseValue}) : super.getSystemProperties({parseValue});
  }
  putSystemProperty({model} = {}) {
    return this.socket.ready ? this.socket.call('v1:system:system-property:update', {data:model}) : super.putSystemProperty({model});
  }
  getEventLogs({count, offset, limit, sort = {by:'', order:'asc'}, startDate, endDate} = {}) {
    if (this.socket.ready) {
      const model = {offset, limit, startDate, endDate};
      if (sort.by && sort.order) model[`${sort.order}${_.capitalize(sort.by)}`] = true;
      return this.socket.call(count ? 'v1:event-logs:count' : 'v1:event-logs:find', model);
    } else {
      return super.getEventLogs({count, offset, limit, sort, startDate, endDate});
    }
  }
  getUsers({functions = false} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:account:findAll', {functions}) : super.getUsers({functions});
  }
  postUser({model} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:account:create', {user:model}) : super.postUser({model});
  }
  putUser({id, model, force = false} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:account:update', {userId:id, user:model, force}) : super.putUser({id, model, force});
  }
  deleteUser({id, force = false} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:account:delete', {userId:id, force}) : super.deleteUser({id, force});
  }
  getUserGroups({functions = false} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:group:findAll', {functions}) : super.getUserGroups({functions});
  }
  postUserGroup({model} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:group:create', {group:model}) : super.postUserGroup({model});
  }
  putUserGroup({id, model, force = false} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:group:update', {groupId:id, group:model, force}) : super.putUserGroup({id, model, force});
  }
  deleteUserGroup({id, force = false} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:group:delete', {groupId:id, force}) : super.deleteUserGroup({id, force});
  }
  getUserRoles({} = {}) {
    return this.socket.ready ? this.socket.call('v1:user:role:findAll') : super.getUserRoles();
  }
}

class LimaCrossOriginWebApi extends LimaWebApi {
  _token = null;
  constructor({baseURL} = {}) {
    super({axiosOpts:{baseURL}});
  }
  async authenticate({model} = {}) {
    const res = await this.postAuth({model, strategy:'local'});
    this.token = res.token;
  }
  get token() {
    // console.log('LimaCrossOriginWebApi: get token', this._token);
    return this._token;
  }
  set token(value) {
    this._token = value;
    // console.log('LimaCrossOriginWebApi: set token', this._token);
  }
}

const limaApi = new LimaApi();

export {LimaCrossOriginWebApi, limaApi as default};
